<script setup lang="ts">

import * as d3 from 'd3';
import { ref, watch } from 'vue';
import type { Quantiles } from '../fin/quantile';
import { TimeSeries, } from '../fin/history';
import * as math from '../fin/math';

interface Props {
  calculationCompletionRatio: number;
  series: TimeSeries<Quantiles>;
  criticalQuantileKey: number;
  retirementYear: number;
  birthYear: number;
  maxYear: number;
  exhaustionYear: number;
}

// "Reactive Props Destructure" to initialise props:
const {
  calculationCompletionRatio = 0,
  series = new TimeSeries<Quantiles>('unknown', []),
  criticalQuantileKey = 0.05,
  retirementYear = 0,
  birthYear = 0,
  maxYear = 0,
  exhaustionYear = 0,
} = defineProps<Props>();

const root = ref<HTMLElement>();

watch( ()=>series, (_newVal, _oldVal ) => {
  console.assert(math.stddev(series.data.at(-1)!.value.values) > 1000);
  upsertChart(calculationCompletionRatio, series);
});

watch( ()=>calculationCompletionRatio, (_newVal, _oldVal ) => {
  upsertChart(calculationCompletionRatio, series);
});

function redraw(): void {
  upsertChart(calculationCompletionRatio, series);
}

defineExpose({redraw});

function sequenceColor(seq: number): string {
  const val = 200 - seq * 12;
  return `rgb(${val},${val},${val})`;
}

function barColour(qtl: number) {
  if (qtl <= 0.01) { return sequenceColor(0); }
  if (qtl <= 0.05) { return sequenceColor(0.5); }
  if (qtl <= 0.10) { return sequenceColor(2); }
  if (qtl <= 0.25) { return sequenceColor(4); }
  if (qtl <= 0.50) { return sequenceColor(8); }
  if (qtl <= 0.75) { return sequenceColor(8); }
  if (qtl <= 0.90) { return sequenceColor(4); }
  if (qtl <= 0.95) { return sequenceColor(2); }
  if (qtl <= 0.99) { return sequenceColor(0.5); }
  if (qtl <= 1.00) { return sequenceColor(0); }
  return 'orange'
}

function plotSeries(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  x: (x:number)=>number,
  y: (y:number)=>number,
  keySpans: Array<{lower: number; upper: number}>,
): void {
  const frameWidthString = svg.node()!.getAttribute('width');
  const frameWidth = +(frameWidthString ?? '1000');

  const yearCount = new Set<number>(
    series.data.map((d => d.year)).flat()
  ).size;

  for (const keyPair of keySpans.reverse()) {
    const data = series.data.map(d => ({
      year: d.year,
      lowerKey: keyPair.lower,
      upperKey: keyPair.upper,
      lowerPercent: Math.round(keyPair.lower * 100),
      upperPercent: Math.round(keyPair.upper * 100),
      lowerValue: d.value.get(keyPair.lower)!,
      upperValue: d.value.get(keyPair.upper)!,
    }));

    const avgPercentile = (keyPair.lower + keyPair.upper)/2;
    const barWidth = Math.max(Math.round(0.8 * frameWidth/yearCount), 2)
                   * (1 - Math.abs(avgPercentile - 0.5));
    const groupOffset = Math.round(barWidth/2);

    svg.append('g')
       .classed('fin-series', true)
       .attr('fill', barColour(keyPair.upper))
       .selectAll()
       .data(data)
       .join('rect')
       .attr('x', d => x(d.year) - groupOffset)
       .attr('width', barWidth)
       .attr('y', d => y(d.upperValue))
       .attr('height', d => Math.max(1, Math.abs(y(d.upperValue) - y(d.lowerValue))))
       .attr('rx', 3)
       .attr('ry', 3)
       .append('title')
       .text(d => series.name + ' '
                              + `${d.lowerPercent}..${d.upperPercent}%`
                              + ' '
                              + d.year
                              + ' £'
                              + Math.round(d.lowerValue).toLocaleString()
                              + '..'
                              + Math.round(d.upperValue).toLocaleString());
  }
}

function plotQuantileLine(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  x: (x:number)=>number,
  y: (y:number)=>number,
  quantileKey: number,
  colour: string,
  title: string,
): void {
  const points: Array<[number, number]> = series.data.map(
    d => [d.year, d.value.get(quantileKey)!]);

  const line = d3.line()
                 .x(d => x(d[0]))
                 .y(d => y(d[1]));

  svg.append('path')
     .attr('class', 'fin-series')
     .attr('visibility', 'visible')
     .attr('fill', 'none')
     .attr('stroke', colour)
     .attr('stroke-width', 3)
     .attr('d', line(points))
     .append('title')
     .text(title);
}

function upsertChart(
  calculationCompletionRatio: number,
  series: TimeSeries<Quantiles>,
) {

  // Declare the chart dimensions and margins.
  const width = 1080;
  const height = 550;
  const marginTop = 40;
  const marginRight = 20;
  const marginBottom = 50;
  const marginLeft = 75;

  const years =  series.data.map(d => d.year).flat();
  const firstYear = Math.min(...years, maxYear, retirementYear);
  const values = series.data.map(d => d.value).flat();
  const minValue = Math.min(...values.map(q=>q.get(0.1)!), 0);
  const maxValue = Math.max(...values.map(q=>q.get(0.5)!), 0);

  const x = d3.scaleLinear()
              .domain([firstYear, maxYear])
              .range([marginLeft, width - marginRight]);

  const xAge = d3.scaleLinear()
                 .domain([firstYear - birthYear, maxYear - birthYear])
                 .range([marginLeft, width - marginRight]);

  // Declare the y (vertical position) scale.
  const y = d3.scaleLinear()
              .domain([minValue, maxValue])
              .range([height - marginBottom, marginTop]);

  // Create the SVG container.
  const svg = d3.create('svg')
                .attr('width', width)
                .attr('height', height)
                .attr('viewBox', [0, 0, width, height])
                .attr('style', 'max-width: 100%; height: auto;');

  // retirement background
  const periods = svg.append('g')
                     .classed('periods', true);

  for( const area of [
    {
      title: 'before retirement period',
      classSuffix: 'pre-retirement',
      start: firstYear,
      end: retirementYear,
      // color: 'rgb(230, 255, 230)',
    }, {
      title: 'funded retirement period (at given confidence percentile)',
      classSuffix: 'funded-retirement',
      start: retirementYear,
      end: exhaustionYear,
      // color: 'rgb(255, 255, 230)',
    }, {
      title: 'unfunded retirement period (at given confidence percentile)',
      classSuffix: 'unfunded-retirement',
      start: exhaustionYear,
      end: maxYear,
      // color: 'rgb(255, 240, 240)',
    },
  ]) {
    periods.append('rect')
      .classed(`time-period ${area.classSuffix}`, true)
      .attr('x', x(area.start))
      .attr('y', 0)
      .attr('width', Math.max(0, x(area.end) - x(area.start)))
      .attr('height', height)
      // .attr('fill', area.color)
      .append('title')
      .text(area.title);
  }

  // Add the x-axis.
  svg.append('g')
     .attr('transform', `translate(0,${height - marginBottom})`)
     .call(d3.axisBottom(x)
             .ticks(width / 80)
             .tickSizeOuter(0)
             .tickFormat(d3.format("d")));

  // Add the xAge-axis.
  svg.append('g')
     .attr('transform', `translate(0,${height - marginBottom + 25})`)
     .call(d3.axisBottom(xAge)
             .tickSizeOuter(5)
             .tickFormat(d3.format("d")));

  // Add the y-axis and label
  svg.append('g')
     .attr('transform', `translate(${marginLeft},0)`)
     .call(d3.axisLeft(y).ticks(height / 40))
     .call((g) => g.select('.domain').remove())
     .call(g => g.append('text')
     .attr('x', -marginLeft)
     .attr('y', 15)
     .attr('fill', 'currentColor')
     .attr('text-anchor', 'start')
     .text('↑ GBP'));

  const keys: number[] = series.data.length > 0 ?
    Array.from((series.data[0]).value.keys).sort() :
    [];

  const keySpans = keys
    .sort()
    .slice(0, -1)
    .map(
      lower => {
        const upper = keys.sort()
                          .find(k => k>lower) ?? 1.0;
        return {
          lower,
          upper,
        };
    }).slice(1,-1);

  plotSeries(svg, x, y, keySpans);

  plotQuantileLine(
    svg, x, y, criticalQuantileKey, 'red',
    `critical quantile ${criticalQuantileKey*100}%`);

  plotQuantileLine(svg, x, y, 0.5, 'navy', 'median: 50%');

  if (calculationCompletionRatio < 0.95) {
    // overlay a semi-transparent rectangle to indicate loading
    svg.append('rect')
      .classed('calculation-incomplete-overlay', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'lightgray')
      .attr('opacity', 0.75);

    svg.append('text')
      .classed('calculation-incomplete-text', true)
      .attr('x', width / 2)
      .attr('y', height / 2)
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'middle')
      .style('font-size', '48px')
      .style('fill', 'darkred')
      .text(`Calculating... ${Math.round(calculationCompletionRatio * 100)}%`);
  }

  for( const child of Array.from(root!.value!.children) ) {
    root?.value?.removeChild(child);
  }

  root?.value?.appendChild(svg.node()!);

  // The title and legend can only be drawn after the svg inserted into DOM and
  // laid out so that we know where the text labels are, and we can place
  // background rectangles correctly.
  addTitle(svg);
  addLegend(svg, width, keySpans);
}

function addLegend(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
  width: number,
  keySpans: Array<{lower: number; upper: number}>,
) {
  const legend = svg.append('g')
    .classed('legend', true)
    .attr('transform', 'translate(' + (width - 325) + ', 10)');

  const textSelections = [...keySpans].map((span, index) => {
    const key = span.upper;
    const prevKey = span.lower;
    const color = barColour(key);
    const percent = Math.round(key*100);
    const prevPercent = Math.round(prevKey*100);
    const name = `${prevPercent}..${percent}%`;

    legend
      .append('rect')
      .classed('legend-bar', true)
      .attr('series-visibility', 'visible')
      .attr('style', 'stroke-width:2;stroke:black;')
      .attr('x', 0)
      .attr('y', index * 18)
      .attr('width', 40)
      .attr('height', 12)
      .attr('fill', color)
      .attr('default-fill', color)
      .attr('series-visible', 'visible');

    const text = legend
      .append('text')
      .classed('legend-text', true)
      .text(name)
      .attr('x', 50)
      .attr('y', 12 + index * 18);

    return text;
  });

  for (const ts of textSelections) {
    addTextBackground(ts, legend);
  }
}

function addTitle(
  svg: d3.Selection<SVGSVGElement, undefined, null, undefined>,
) {
  const titleGroup = svg.append('g');

  const titleText = titleGroup.append('text')
    .text(series.name.replace('_', ' ') + ' Quantiles')
    .style("font-size", "34px")
    .classed('title-text', true)
    .attr('alignment-baseline', 'top')
    .attr('text-anchor', 'start')
    .attr('x', 300)
    .attr('y', 25);

  addTextBackground(titleText, titleGroup);
}

async function addTextBackground(
  text: d3.Selection<SVGTextElement, undefined, null, undefined>,
  container: d3.Selection<SVGSVGElement | SVGGElement, undefined, null, undefined>,
) {
  const padding = 4;
  const node = text.node() as SVGGraphicsElement;
  if (!node) return;

  const bbox = node.getBBox();

  container
    .insert('rect', ':first-child')
    .attr('x', bbox.x - padding)
    .attr('y', bbox.y - padding)
    .attr('width', bbox.width + padding * 2)
    .attr('height', bbox.height + padding * 2)
    .style('fill', 'white')
    .attr('opacity', '0.9');
}

</script>

<template>
  <div class="fin-plot fin-plot:money-quantiles-time output-card">
    <div
      ref="root"
    />
  </div>
</template>

<style scoped>
</style>
